/**
 * Componente modal para retirada de cotas
 * 
 * Responsável por:
 * - Permitir que o vendedor escolha a quantidade a retirar
 * - Validar a quantidade disponível
 * - Enviar solicitação de distribuição direta
 * 
 * @module App/UnityDistributions/WithdrawModal
 */
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { toast } from "sonner";
import { distributeDirectlyToSellers } from "@/Api/distributions";
import { Loader2, Download } from "lucide-react";
import { useState } from "react";

interface WithdrawModalProps {
  unity_id: number;
  contest_id: number;
  contest_product_classification_id: number;
  contest_name: string;
  product_name: string;
  available_quantity: number;
  seller_id: number;
  onSuccess: () => void;
  children?: React.ReactNode;
}

const createFormSchema = (maxQuantity: number) =>
  z.object({
    quantity: z
      .string()
      .min(1, "Quantidade é obrigatória")
      .refine((val) => {
        const num = parseInt(val);
        return num > 0 && num <= maxQuantity;
      }, `Quantidade deve ser entre 1 e ${maxQuantity}`),
  });

export function WithdrawModal({
  unity_id,
  contest_id,
  contest_product_classification_id,
  contest_name,
  product_name,
  available_quantity,
  seller_id,
  onSuccess,
  children,
}: WithdrawModalProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const formSchema = createFormSchema(available_quantity);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      quantity: available_quantity.toString(),
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setLoading(true);

      const quantity = parseInt(values.quantity);

      const { data, ok } = await distributeDirectlyToSellers({
        unity_id,
        contest_id,
        contest_product_classification_id,
        sellers: [
          {
            id: seller_id,
            quota_quantity: quantity,
          },
        ],
      });

      if (ok) {
        toast.success(
          data?.message || `${quantity} cotas retiradas com sucesso!`
        );
        onSuccess();
        setOpen(false);
        form.reset();
      } else {
        toast.error(data?.message || "Erro ao retirar cotas!");
      }
    } catch (error) {
      console.error(error);
      toast.error("Ocorreu um erro ao processar sua solicitação.");
    } finally {
      setLoading(false);
    }
  };

  const handleQuickSelect = (percentage: number) => {
    const quantity = Math.floor((available_quantity * percentage) / 100);
    form.setValue("quantity", quantity.toString());
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button
            size="sm"
            variant="ghost"
            className="text-green-600 hover:text-green-700 hover:bg-green-50"
          >
            <Download className="h-4 w-4 mr-1" />
             Ver cotas disponíveis
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Retirar Cotas</DialogTitle>
          <DialogDescription className="space-y-1">
            <div>
              <strong>Concurso:</strong> {contest_name}
            </div>
            <div>
              <strong>Produto:</strong> {product_name}
            </div>
            <div>
              <strong>Disponível:</strong>{" "}
              <span className="text-green-600 font-medium">
                {available_quantity} cotas
              </span>
            </div>
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="quantity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Quantidade a Retirar</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="1"
                      max={available_quantity}
                      placeholder="Digite a quantidade"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <label className="text-sm font-medium">Seleção Rápida:</label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="bg-green-500 text-white hover:bg-green-600"
                  onClick={() => handleQuickSelect(25)}
                >
                  25%
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="bg-green-500 text-white hover:bg-green-600"
                  size="sm"
                  onClick={() => handleQuickSelect(50)}
                >
                  50%
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="bg-green-500 text-white hover:bg-green-600"

                  onClick={() => handleQuickSelect(75)}
                >
                  75%
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="bg-green-500 text-white hover:bg-green-600"
                  onClick={() => handleQuickSelect(100)}
                >
                  100%
                </Button>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                className="bg-red-500 text-white hover:bg-red-600"
                onClick={() => setOpen(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} className="bg-primary hover:bg-primary/90">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Confirmar Retirada
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
} 